import {
  Alert,
  BodyText,
  Button,
  Checkbox,
  Flex,
  TextInput,
} from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';
import { FindStatus, IOTSProvider } from '@src/types/oneTouchSwitching';
import { YourDetailsFormValues } from '@src/types/yourDetails';
import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter';
import { validateGBLandlineNumber } from '@utils/validators';

import { ProviderSelect } from '../../ProviderSelect';
import './styles.scss';

interface NotFoundDialogProps {
  open: boolean;
  providers: IOTSProvider[];
  canUpdateState: boolean;
  onRetry?: (surname?: string) => void;
}

const toOtsKey = (field: string): keyof YourDetailsFormValues =>
  `ots${field.charAt(0).toUpperCase()}${field.slice(1)}` as keyof YourDetailsFormValues;

const NotFoundDialog: React.FC<NotFoundDialogProps> = ({
  open,
  providers,
  canUpdateState,
  onRetry,
}) => {
  const { values, setFieldValue, setValues } =
    useFormikContext<YourDetailsFormValues>();
  const { setOpen, id: dialogId } = useDialog(DialogID.NOT_FOUND_OTS);
  const [stateChanged, setStateChanged] = useState(false);

  const {
    otsSelectedProvider: selectedProvider,
    otsProviderNotListed: providerNotListed,
    otsFieldErrors: fieldErrors,
    otsHomePhoneReference: homePhoneReference,
    aboutNameLast: surname,
  } = values;

  useEffect(() => {
    if (canUpdateState && open) {
      setOpen(true);
    } else if (canUpdateState) {
      setOpen(false);
    }
  }, [canUpdateState, open, setOpen]);

  const setProviderNotListed = (value: boolean) => {
    setValues((prev) => ({
      ...prev,
      otsProviderNotListed: value,
      otsSelectedProvider: null,
    }));
  };

  const setProvider = (value: IOTSProvider | null) =>
    setFieldValue('otsSelectedProvider', value);

  const setField = (field: string, value: string) => {
    const otsKey = toOtsKey(field);

    if (value !== values[otsKey]) {
      setStateChanged(true);
    }
    setFieldValue(otsKey, value);
  };

  const getFieldValue = (field: string): string => {
    const otsKey = toOtsKey(field);
    return (values[otsKey] as string) || '';
  };

  const onContinue = () => {
    if (!stateChanged) {
      setFieldValue('otsFindStatus', FindStatus.FAIL);
    } else if (providerNotListed) {
      setFieldValue('otsFindStatus', FindStatus.NOT_STARTED);
    } else if (onRetry) {
      onRetry(values.otsSurname as string);
    } else {
      setFieldValue('otsFindStatus', FindStatus.FINDING);
    }
    setStateChanged(false);
  };

  const fieldValid = (field: string) => {
    switch (field) {
      case 'surname':
        return !!surname;
      case 'homePhoneReference':
        return validateGBLandlineNumber(homePhoneReference ?? '');
      default:
        return true;
    }
  };

  const formValid = fieldErrors.reduce((p, c) => p && fieldValid(c), true);

  return (
    <Dialog
      id={dialogId}
      heading={`We couldn't find your ${selectedProvider?.name ?? ''} account`}
      image={<img src="oneTouchSwitching/warning.svg" height={48} width={48} />}
      hideCloseButton
      preventDismiss
    >
      <Flex direction="column" gap="300">
        <BodyText>
          Please confirm your details below, so we can make your switch as
          smooth as possible and prevent you from paying for two broadband
          services at the same time.
        </BodyText>
        <ProviderSelect
          providers={providers}
          selectedProvider={selectedProvider}
          setSelectedProvider={setProvider}
          disabled={providerNotListed}
        />
        <Checkbox
          label="My provider isn’t listed"
          checked={providerNotListed}
          onCheckedChange={setProviderNotListed}
        />
        {fieldErrors.map((field) => (
          <TextInput
            id={field}
            key={field}
            label={capitalizeFirstLetter(field)}
            value={getFieldValue(field)}
            onChange={(e) => setField(field, e.target.value)}
            validationStatus={fieldValid(field) ? 'valid' : 'invalid'}
          />
        ))}
        {providerNotListed && (
          <Alert
            colorScheme="warning"
            title="Please check the list of providers again so we can make your switch as smooth as possible. If you can't find your provider on the list, you can still confirm your order with us, but you'll need to contact your current provider yourself to cancel your current service and understand any implications."
          />
        )}
      </Flex>
      <DialogActions>
        <Button
          onClick={onContinue}
          disabled={!surname || !formValid}
          variant="solid"
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export type { NotFoundDialogProps };
export { NotFoundDialog };
